import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useParams } from 'react-router';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import React, { useMemo, useState } from 'react';
import { SelectOption } from 'apps/sigma2-ui/src/components/ui/SelectComponent/SimpleSelect';
import { SimpleSelect } from 'apps/sigma2-ui/src/components/ui/SelectComponent/SimpleSelect';
import { AdminOrganizations } from 'libs/end-to-end/src/admin';
import { Organizations } from 'libs/end-to-end/src/types/organizations/organizations';
import { CreateSsoIdentityProviderDataOidc } from 'libs/end-to-end/src/sso';
import { CreateSsoIdentityProviderDataSaml } from 'libs/end-to-end/src/sso';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { SSOIdentityProvider } from 'libs/end-to-end/src/sso';
import { UpdateSsoIdentityProviderDataOidc } from 'libs/end-to-end/src/sso';
import { UpdateSsoIdentityProviderDataSaml } from 'libs/end-to-end/src/sso';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { useToggle } from 'apps/sigma2-ui/src/lib/hooks/useToggle';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import classNames from 'classnames';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

const idpOptions: SelectOption<'oidc' | 'saml'>[] = [
  {
    label: 'OpenID Connect',
    value: 'oidc',
  },
  {
    label: 'SAML',
    value: 'saml',
  },
];

export const AdminSSOIdentityProviderPage = () => {
  const { myUser, userAuthenticatedApiCall } = useUser();
  const navigate = useNavigateSafe();
  const { identityProviderId } = useParams();

  const isNewIDP = identityProviderId === 'new';

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);

  const [selectedIDP, setSelectedIDP] = useState<'oidc' | 'saml'>('oidc');

  const [idpName, setIdpName] = useState<string>('');

  // OIDC
  const [clientId, setClientId] = useState<string>('');
  const [clientSecret, setClientSecret] = useState<string>('');
  const [issuerUrl, setIssuerUrl] = useState<string>('');
  const [scopes, setScopes] = useState<string>('');

  const [selectedOrganizationId, setSelectedOrganizationId] = useState<
    string | null
  >(null);

  const [organizations, setOrganizations] = useState<Organizations | null>(
    null,
  );

  const [emailClaimMappingValue, setEmailClaimMappingValue] =
    useState<string>('');

  const [groupsClaimMappingEnabled, setGroupsClaimMappingEnabled] = useToggle({
    initialState: false,
  });
  const [groupsClaimMappingValue, setGroupsClaimMappingValue] = useState<
    string | null
  >(null);

  const [publicDomainValue, setPublicDomainValue] = useState<string>('');

  const groupsClaimMappingValueToSend = useMemo(() => {
    if (
      groupsClaimMappingEnabled &&
      groupsClaimMappingValue &&
      groupsClaimMappingValue.length > 0
    ) {
      return groupsClaimMappingValue;
    } else {
      return undefined;
    }
  }, [groupsClaimMappingEnabled, groupsClaimMappingValue]);

  // SAML
  const [samlMetadataUrl, setSamlMetadataUrl] = useState<string>('');

  // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
  useAsyncEffect(
    async (isAlive) => {
      if (
        !isAlive() ||
        !myUser ||
        myUser.roleAssignments !== 'superAdmin' ||
        hasLoadedOnce
      ) {
        return;
      }

      setHasLoadedOnce(true);

      setIsLoadingOrganizations(true);
      const getResult = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/organizations`,
        requestOptions: {
          method: 'GET',
        },
        responseSchema: Organizations,
      });

      setIsLoadingOrganizations(false);

      setOrganizations(getResult?.resultType === 'ok' ? getResult.value : null);

      if (!isNewIDP) {
        const getResult = await userAuthenticatedApiCall({
          url: `${getApiBaseUrl()}/admin/sso/identity-providers/${identityProviderId}`,
          requestOptions: {
            method: 'GET',
          },
          responseSchema: SSOIdentityProvider,
        });

        if (!isAlive()) {
          return;
        }

        if (!getResult) {
          return;
        }

        if (getResult.resultType === 'err') {
          return;
        }

        if (getResult.value.type === 'oidc') {
          const { name, type, clientId, issuerUrl, scopes } = getResult.value;
          setIdpName(name);
          setSelectedIDP(type);
          setClientId(clientId);
          setIssuerUrl(issuerUrl);
          setScopes(scopes);
        } else if (getResult.value.type === 'saml') {
          const { name, type, metadataUrl } = getResult.value;
          setIdpName(name);
          setSelectedIDP(type);
          setSamlMetadataUrl(metadataUrl);
        }
        setEmailClaimMappingValue(getResult.value.emailClaimMapping);
        const isGroupClaimMappingCurrentlyEnabled =
          getResult.value.groupsClaimMapping !== undefined;

        if (groupsClaimMappingEnabled !== isGroupClaimMappingCurrentlyEnabled) {
          setGroupsClaimMappingEnabled();
        }

        setGroupsClaimMappingValue(getResult.value.groupsClaimMapping ?? null);

        setSelectedOrganizationId(getResult.value.organizationId);

        setPublicDomainValue(getResult.value.publicSsoIdentifier);
      }
    },
    [
      groupsClaimMappingEnabled,
      hasLoadedOnce,
      identityProviderId,
      isNewIDP,
      myUser,
      setGroupsClaimMappingEnabled,
      userAuthenticatedApiCall,
    ],
  );

  // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
  const onSave = useAsyncCallback(
    async (isAlive) => {
      if (
        !isAlive() ||
        !myUser ||
        myUser.roleAssignments !== 'superAdmin' ||
        isLoadingOrganizations ||
        !selectedOrganizationId
      ) {
        return;
      }

      let stringifiedBody: string;
      if (isNewIDP && selectedIDP === 'oidc') {
        const body: CreateSsoIdentityProviderDataOidc = {
          name: idpName,
          type: 'oidc',
          clientId,
          clientSecret,
          issuerUrl,
          scopes,
          organizationId: selectedOrganizationId,
          emailClaimMapping: emailClaimMappingValue,
          groupsClaimMapping: groupsClaimMappingValueToSend,
        };
        stringifiedBody = JSON.stringify(body);
      } else if (isNewIDP && selectedIDP === 'saml') {
        const body: CreateSsoIdentityProviderDataSaml = {
          name: idpName,
          type: 'saml',
          organizationId: selectedOrganizationId,
          metadataUrl: samlMetadataUrl,
          emailClaimMapping: emailClaimMappingValue,
          groupsClaimMapping: groupsClaimMappingValueToSend,
        };
        stringifiedBody = JSON.stringify(body);
      } else if (!isNewIDP && selectedIDP === 'oidc') {
        const body: UpdateSsoIdentityProviderDataOidc = {
          type: 'oidc',
          clientId,
          issuerUrl,
          scopes,
          emailClaimMapping: emailClaimMappingValue,
          groupsClaimMapping: groupsClaimMappingValueToSend,
        };
        stringifiedBody = JSON.stringify(body);
      } else if (!isNewIDP && selectedIDP === 'saml') {
        const body: UpdateSsoIdentityProviderDataSaml = {
          type: 'saml',
          metadataUrl: samlMetadataUrl,
          emailClaimMapping: emailClaimMappingValue,
          groupsClaimMapping: groupsClaimMappingValueToSend,
        };
        stringifiedBody = JSON.stringify(body);
      } else {
        return;
      }

      const baseUrl = isNewIDP
        ? `${getApiBaseUrl()}/admin/sso/identity-providers`
        : `${getApiBaseUrl()}/admin/sso/identity-providers/${identityProviderId}`;
      const method = isNewIDP ? 'POST' : 'PUT';

      const result = await userAuthenticatedApiCall({
        url: baseUrl,
        requestOptions: {
          method: method,
          body: stringifiedBody,
        },
        responseSchema: EmptyObject,
      });

      if (!isAlive()) {
        return;
      }

      if (!result) {
        return;
      }

      if (result.resultType === 'err') {
        return;
      }

      navigate('/sso-main');
    },
    [
      clientId,
      clientSecret,
      emailClaimMappingValue,
      groupsClaimMappingValueToSend,
      identityProviderId,
      idpName,
      isLoadingOrganizations,
      isNewIDP,
      issuerUrl,
      myUser,
      navigate,
      samlMetadataUrl,
      scopes,
      selectedIDP,
      selectedOrganizationId,
      userAuthenticatedApiCall,
    ],
  );

  const oidcUI = useMemo(() => {
    return (
      <div className="py-6">
        <div className={classNames(isNewIDP && 'grid grid-cols-2 gap-4')}>
          <div className="justify-self-stretch">
            <div>Client ID</div>
            <TextInput
              data-qa="clientIdInput"
              type="text"
              label="Client ID"
              value={clientId}
              onChange={(e) => setClientId(e.currentTarget.value)}
            />
          </div>
          {isNewIDP && (
            <div>
              <div>Client Secret</div>
              <TextInput
                data-qa="clientSecretInput"
                type="text"
                label="Client Secret"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.currentTarget.value)}
              />
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div>Issuer URL</div>
            <TextInput
              data-qa="issuerUrlInput"
              type="text"
              label="Issuer URL"
              value={issuerUrl}
              onChange={(e) => setIssuerUrl(e.currentTarget.value)}
            />
          </div>
          <div>
            <div>Scopes</div>
            <TextInput
              data-qa="scopesInput"
              type="text"
              label="Scopes"
              value={scopes}
              onChange={(e) => setScopes(e.currentTarget.value)}
            />
          </div>
        </div>
      </div>
    );
  }, [clientId, clientSecret, isNewIDP, issuerUrl, scopes]);

  const samlUi = useMemo(() => {
    return (
      <div className="py-6">
        <div>Metadata URL</div>
        <TextInput
          data-qa="samlMetadataUrlInput"
          type="text"
          label="SAML Metadata URL"
          value={samlMetadataUrl}
          onChange={(e) => setSamlMetadataUrl(e.currentTarget.value)}
        />
      </div>
    );
  }, [samlMetadataUrl]);

  // TODO: The complexity of this code is greater than 10. Refer to https://eslint.org/docs/latest/rules/complexity
  const ui = useMemo(() => {
    return (
      <>
        <div className="flex flex-col gap-1 px-6">
          {identityProviderId && (
            <div>
              <h1 className="text-lg font-bold">
                {isNewIDP ? 'New Identity Provider' : 'Edit Identity Provider'}
              </h1>
            </div>
          )}
          {!isNewIDP && (
            <div>
              <LegacyButton
                onClick={() =>
                  navigate(
                    `/sso-main/identity-provider/${identityProviderId}/role-mappings`,
                  )
                }
                variant={LegacyButtonVariant.PRIMARY_DARK}
              >
                Display Role Mappings
              </LegacyButton>
            </div>
          )}
          <div>
            <label>Name</label>
            <TextInput
              data-qa="nameInput"
              type="text"
              label="Name"
              value={idpName}
              onChange={(e) => setIdpName(e.currentTarget.value)}
              disabled={!isNewIDP}
            />
            {!isNewIDP && (
              <>
                <label>Public Domain Hint</label>
                <TextInput
                  data-qa="publicDomainInput"
                  type="text"
                  label="Public Domain"
                  value={publicDomainValue}
                  disabled={true}
                  onChange={() => {}}
                />
              </>
            )}
          </div>
          <div className="pt-6">
            <h1 className="text-lg font-bold">
              {isNewIDP
                ? 'Choose Provider Type (OIDC or SAML)'
                : 'Provider Type'}
            </h1>
            <SimpleSelect
              selectedOptionValue={selectedIDP}
              options={idpOptions}
              updateOptions={(value) => setSelectedIDP(value)}
              disabled={!isNewIDP}
            />
          </div>
          {selectedIDP === 'oidc' && oidcUI}
          {selectedIDP === 'saml' && samlUi}

          <h1 className="text-lg font-bold">Claim Mappings</h1>
          <p>
            Map your identity providers claim/attribute names to a corresponding
            SigmaSquared attribute.
          </p>
          <div className="pt-2">
            <div className="flex items-center">
              <div className="font-bold pr-4">Email</div>
              <TextInput
                className=" border-1 border-tertiary focus:border-tertiary focus:ring-offset-2 focus:ring-offset-blue-1000 focus:ring-blue-300 focus:ring-1 font-sans mt-1 px-3 py-3 rounded-lg text-base text-gray-1000 transition w-auto"
                data-qa="emailClaimMappingInput"
                type="text"
                label="Email Claim Mapping"
                value={emailClaimMappingValue}
                onChange={(e) =>
                  setEmailClaimMappingValue(e.currentTarget.value)
                }
              />
            </div>

            <div className="flex items-center pt-3">
              <div className="pr-2">Enable Group Mappings</div>
              <input
                type="checkbox"
                className="form-checkbox text-tertiary"
                checked={groupsClaimMappingEnabled}
                onClick={setGroupsClaimMappingEnabled}
              />
            </div>

            {groupsClaimMappingEnabled && (
              <div className="flex items-center">
                <div className="font-bold pr-4">Groups</div>
                <TextInput
                  className=" border-1 border-tertiary focus:border-tertiary focus:ring-offset-2 focus:ring-offset-blue-1000 focus:ring-blue-300 focus:ring-1 font-sans mt-1 px-3 py-3 rounded-lg text-base text-gray-1000 transition w-auto"
                  data-qa="groupsClaimMappingInput"
                  type="text"
                  label="Groups Claim Mapping"
                  value={groupsClaimMappingValue ?? ''}
                  onChange={(e) =>
                    setGroupsClaimMappingValue(e.currentTarget.value)
                  }
                />
              </div>
            )}

            <div className="flex flex-row mb-2 pt-6">
              <div className="basis-1/2">
                <label className="block mb-1">
                  Organization:{' '}
                  {isNewIDP && (
                    <select
                      onChange={(e) =>
                        setSelectedOrganizationId(
                          e.target.value === '' ? null : e.target.value,
                        )
                      }
                      defaultValue={
                        selectedOrganizationId === null
                          ? ''
                          : selectedOrganizationId
                      }
                      className="form-select border-1 border-tertiary focus:border-tertiary focus:ring-offset-2 focus:ring-offset-blue-1000 focus:ring-blue-300 focus:ring-1 font-sans mt-1 px-3 py-3 rounded-lg text-base text-gray-1000 w-full transition"
                    >
                      <>
                        <option key={'none'} value={''}>
                          Select an organization...
                        </option>
                        {organizations?.organizations.map((organization) => (
                          <option key={organization.id} value={organization.id}>
                            {organization.name}
                          </option>
                        ))}
                      </>
                    </select>
                  )}
                  {!isNewIDP && (
                    <p>
                      {organizations?.organizations.find(
                        (o) => o.id === selectedOrganizationId,
                      )?.name ?? ''}
                    </p>
                  )}
                </label>
              </div>
            </div>
          </div>

          <div>
            <LegacyButton
              onClick={onSave}
              variant={LegacyButtonVariant.PRIMARY_DARK}
            >
              Save
            </LegacyButton>
          </div>
        </div>
      </>
    );
  }, [
    emailClaimMappingValue,
    groupsClaimMappingEnabled,
    groupsClaimMappingValue,
    identityProviderId,
    idpName,
    isNewIDP,
    navigate,
    oidcUI,
    onSave,
    organizations?.organizations,
    samlUi,
    selectedIDP,
    selectedOrganizationId,
    setGroupsClaimMappingEnabled,
    publicDomainValue,
  ]);

  return (
    <AdminLayout currentPage={'AdminSSOPage'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'SSO', to: '/sso-main' },
          {
            label: isNewIDP
              ? 'New Identity Provider'
              : 'Edit Identity Provider',
            to: `/sso-main/identity-provider/${identityProviderId}/role-mappings`,
          },
        ]}
      />
      {ui}
    </AdminLayout>
  );
};
